import NiceModal from '@ebay/nice-modal-react';

import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import SearchInput from 'components/field/SearchInput';
import SwitchInput from 'components/field/SwitchInput';
import { NonFormContainer } from 'components/form/FormLayout.styled';
import Checkbox from 'components/global/Checkbox';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import LottiePlayer from 'components/global/LottiePlayer';
import Popper from 'components/global/Popper';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useDebounce from 'hooks/useDebounce';
import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import { useNavigateSearch } from 'hooks/useNavigateSearch';

import { Menu, MenuItem } from 'pages/SalesChannels/ConnectedStores/ConnectedStores.styled';

import { SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import PATHS, { mergePaths } from 'utils/paths';

import { faTag, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import {
	faEdit,
	faEllipsis,
	faReplyClock,
	faShippingFast,
	faTowerCell,
	faTruck
} from '@fortawesome/pro-solid-svg-icons';

import * as S from './ConnectedPartners.styled';
import { getConnectedPartnersList, setConnectedPartners } from './ConnectedPartnersSlice.list';

/**
 *
 * @author Burak Ak
 */

const Connected = () => {
	const { showError } = useAlert();
	const [isLoading, setIsLoading] = useState(true);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [filteredList, setFilteredList] = useState([]);
	const [showActives, setShowActives] = useState(true);
	const connectedPartnersList = useSelector((state) => state?.connectedPartners);

	const [searchValue, setSearchValue] = useState(null);
	const debouncedValue = useDebounce(searchValue, 500);

	useEffect(() => {
		dispatch(getConnectedPartnersList())
			.unwrap()
			.then(() => {
				setIsLoading(false);
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	}, []);

	useEffect(() => {
		let filtered = connectedPartnersList;
		if (debouncedValue) {
			filtered = filtered?.filter(({ name, nameAR, subname }) => {
				const localeName = name?.toLocaleLowerCase();
				const localeNameAR = nameAR?.toLocaleLowerCase();
				const localeSubname = subname?.toLocaleLowerCase();
				const localeValue = debouncedValue?.toLocaleLowerCase();

				return (
					localeName?.includes(localeValue) ||
					localeName?.split(' ')?.join('')?.includes(localeValue) ||
					localeNameAR?.includes(localeValue) ||
					localeNameAR?.split(' ')?.join('')?.includes(localeValue) ||
					localeSubname?.includes(localeValue) ||
					localeSubname?.split(' ')?.join('')?.includes(localeValue)
				);
			});
		}
		if (showActives) {
			filtered = filtered?.filter((item) => item.active);
		}
		setFilteredList({ data: filtered });
	}, [debouncedValue, connectedPartnersList, showActives]);

	return (
		<>
			<NonFormContainer>
				<S.SubFormHeaderContainer
					lordIcon="https://storage.googleapis.com/tryoto-public/lottie-files-compressed/shippingPartners-icon-new.json.gz"
					icon={faShippingFast}
					trigger="hover"
					boldTitle="connectedDeliveryCompanies"
					lightTitle="connectedDeliveryCompaniesSubtitle"
					className="table-lord-icon"
					justifyContent="space-evenly">
					<S.CheckboxContainer>
						<Checkbox
							checked={showActives}
							onClick={() => {
								setShowActives(!showActives);
							}}
						/>
						<Typography noWrap>showActiveOnly</Typography>
					</S.CheckboxContainer>
				</S.SubFormHeaderContainer>

				<S.InputContainer>
					<SearchInput
						setValue={setSearchValue}
						value={searchValue}
						variant="grey"
						placeholder="searchKeyword"
						fullWidth
					/>
				</S.InputContainer>

				{isLoading ? (
					<S.PartnersContainer>
						{[...Array(5)].map((_, i) => (
							<S.LoadingRowSpaced key={i}>
								<S.LoadingRowCentered>
									<div className="p-3">
										<Skeleton width="6rem" height="6rem" />
									</div>

									<div>
										<Skeleton className="loadingHeaderPadding" width="7rem" height="1rem" />
										<Skeleton className="loadingHeaderPadding" width="9rem" height="1rem" />
									</div>
								</S.LoadingRowCentered>

								<S.LoadingRowCenteredActions>
									<Skeleton width="4rem" height="1.5rem" />
									<Skeleton width="7.5rem" height="3rem" />
									<Skeleton width="1.7rem" height="2.5rem" />
								</S.LoadingRowCenteredActions>
							</S.LoadingRowSpaced>
						))}
					</S.PartnersContainer>
				) : (
					<S.PartnersContainer>
						{filteredList?.data?.length === 0 || filteredList?.data?.length === undefined ? (
							<S.PartnersContainer className="mt-5">
								<LottiePlayer name="shipping-partners-connected-empty" width="auto" height="300px" />
								<div style={{ textAlign: 'center' }}>
									<Typography className="emptySubtitle" noWrap>
										emptyConnectedShippingPartnersSubtitle
									</Typography>
									<Button
										style={{ marginLeft: 'Auto', marginRight: 'Auto' }}
										icon={faTruck}
										label="connectNow"
										className="mb-3"
										onClick={() =>
											navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.MORE_CONNECTION))
										}
									/>
								</div>
							</S.PartnersContainer>
						) : (
							<>
								{filteredList?.data?.map((data, index) => (
									<PartnerBox key={index} {...data} />
								))}
							</>
						)}
					</S.PartnersContainer>
				)}
			</NonFormContainer>
		</>
	);
};

const PartnerBox = ({ integrationName, icon, name, nameAR, omniAggregator, subname, active, ID, settingsID }) => {
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const navigateSearch = useNavigateSearch();
	const [menuButtonRef, setMenuButtonRef] = useState(null);
	const { showError, showSuccess } = useAlert();
	const isAuthorized = useAccessControl();
	const connectedPartnersList = useSelector((state) => state?.connectedPartners);

	const statusChange = async (ID) => {
		try {
			const response = await axiosPrivate.post(SHIPPING_PARTNERS.UPDATE_DELIVERY_CHANNEL_SETTINGS, {
				settingsID: ID,
				settings: {
					active: !active
				}
			});

			if (response.status === 200) {
				changeLocalStatus(settingsID, !active);
				showSuccess();
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			if (error.response?.data.code === 'error.F014') {
				navigate(PATHS.OTO_MODULES);
				showError({
					header: 'info.limitedShippingPartnersTitle',
					message: 'info.limitedShippingPartnersDescription'
				});
			} else {
				showError({
					message: error.response?.data.code || error.response?.data.message
				});
				AppLogger.error(error);
			}
		}
	};

	const changeLocalStatus = (ID, requestActive) => {
		const connectedPartnersListDummy = JSON.parse(JSON.stringify(connectedPartnersList));
		const index = connectedPartnersListDummy.findIndex((item) => item.settingsID === ID);
		connectedPartnersListDummy[index].active = requestActive;
		dispatch(setConnectedPartners(connectedPartnersListDummy));
	};

	const deleteRow = async (ID) => {
		try {
			const response = await axiosPrivate.post(SHIPPING_PARTNERS.DELETE_CONNECTED_DELIVERY_CHANNEL, {
				settingsID: ID
			});
			if (response.status === 200) {
				showSuccess();
				dispatch(setConnectedPartners(connectedPartnersList.filter((item) => item.settingsID !== ID)));
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			showError({
				message: error.response?.data.code || error.response?.data.message
			});
			AppLogger.error(error);
		}
	};

	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const { LanguageCode } = useLocale();

	const handleClick = (e, closePopper, form) => {
		e.stopPropagation();
		if (form) {
			e.preventDefault();
			NiceModal.show(form, { settingsID, icon, integrationName, name });
		}
		closePopper();
	};

	const handleOpenModal = (e, closePopper, form) => {
		e.stopPropagation();
		if (form) {
			e.preventDefault();
			NiceModal.show(form, {
				title: 'areYouSureYouWantToDeleteThis',
				subTitle: 'thisWillDeleteTheSelectedItemPermanently',
				rejectLabel: 'no',
				confirmLabel: 'yes',
				yesAction: () => deleteRow(settingsID)
			});
		}
		closePopper();
	};

	return (
		<S.RowSpaced>
			<S.RowCentered>
				<div className="p-3">
					<Image url={icon} height="6rem" width="6rem" objectFit="scale-down" radius={4} />
				</div>

				<div style={{ width: isMobile ? '10rem' : '150px' }}>
					<Typography noWrap={true} variant={isMobile ? 'p3' : 'p1'} fontWeight="bold">
						{LanguageCode === 'arabicValue' ? (nameAR ?? name) : name}
					</Typography>
					<Typography noWrap={true}>{subname}</Typography>
				</div>
			</S.RowCentered>
			<S.RowCenteredActions>
				<SwitchInput
					style={{ margin: '0 1rem' }}
					value={active}
					disabled={omniAggregator}
					setValue={() => {
						statusChange(settingsID);
					}}
				/>
				<Button
					variant="blackOutline"
					icon={faEdit}
					label={isMobile ? '' : 'edit'}
					style={{ margin: isMobile ? 0 : '0 1rem' }}
					iconPos="right"
					disabled={omniAggregator}
					disabledTooltip={omniAggregator && 'errorMsg.notEditableForOmniAggregator'}
					onClick={() =>
						navigateSearch('/shipping-partners/shipping-partner-form', {
							mode: 'edit',
							settingsID,
							id: ID,
							img: icon,
							name: LanguageCode === 'arabicValue' ? (nameAR ?? name) : name,
							integrationName
						})
					}
					size="small"
				/>
				<Button
					ref={setMenuButtonRef}
					icon={faEllipsis}
					variant="transparent"
					size="large"
					iconSize="medium"
					iconButton
				/>
				{menuButtonRef && (
					<Popper refElement={menuButtonRef} timeout={100}>
						{(closePopper) => (
							<Menu id="rowMenuContainer">
								{isMobile && (
									<Typography variant="p1" className="px-4" fontWeight="500">
										{subname}
									</Typography>
								)}

								{isAuthorized(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CITY_COVERAGE)) && (
									<MenuItem
										onContextMenu={(e) => handleClick(e, closePopper)}
										onClick={() => {
											navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CITY_COVERAGE), {
												state: {
													id: ID,
													settingsID,
													icon,
													integrationName,
													name,
													subname
												}
											});
										}}>
										<Icon icon={faTowerCell} />
										<Typography>cityCoverage</Typography>
									</MenuItem>
								)}

								{isAuthorized('SLAModule') && (
									<MenuItem
										onContextMenu={(e) => handleClick(e, closePopper)}
										onClick={() =>
											navigate(PATHS.SHIPPING_PARTNER_SLA_FORM, {
												state: {
													settingsID,
													icon,
													integrationName,
													name,
													subname
												}
											})
										}>
										<Icon icon={faReplyClock} />
										<Typography>slaConfigurations</Typography>
									</MenuItem>
								)}

								{isAuthorized('ShippingAccountingModule') && (
									<MenuItem
										onContextMenu={(e) => handleClick(e, closePopper)}
										onClick={() =>
											navigate(mergePaths(PATHS.SHIPPING_PRICE, PATHS.SHIPPING_PRICES))
										}>
										<Icon icon={faTag} />
										<Typography>shippingPrices</Typography>
									</MenuItem>
								)}
								{isAuthorized(PATHS.SHIPPING_PARTNER_DELIVERY_SLOT_CONFIGURATION_FORM) && (
									<MenuItem
										onContextMenu={(e) => handleClick(e, closePopper)}
										onClick={() =>
											navigate(PATHS.SHIPPING_PARTNER_DELIVERY_SLOT_CONFIGURATION_FORM, {
												state: {
													settingsID,
													icon,
													integrationName,
													name,
													subname
												}
											})
										}>
										<Icon icon={faReplyClock} />
										<Typography>deliverySlotConfiguration</Typography>
									</MenuItem>
								)}
								<MenuItem
									onContextMenu={(e) => handleOpenModal(e, closePopper, ConfirmationModal)}
									onClick={(e) => handleOpenModal(e, closePopper, ConfirmationModal)}>
									<Icon icon={faTrashCan} />
									<Typography>delete</Typography>
								</MenuItem>
							</Menu>
						)}
					</Popper>
				)}
			</S.RowCenteredActions>
		</S.RowSpaced>
	);
};

export default Connected;
